import {useContext, useEffect} from 'react';
import {runInAction} from "mobx";

import ajaxGetQueue, {watsonGetQueue} from "../backends/AjaxQueue";
import { DefaultController } from "../backends/YiiRoutes";
import {isJson} from "../components/admin/spinup/utilities";
import {StoreContext} from "../stores/StoreLoader";
import WatsonApi, {buildSwaggerAuthorization} from "../backends/WatsonApi";
import useAsyncEffect from "./useAsyncEffect";

export default function useFinalizeBlock(blockObj, isPreviewBlock) {
    const {i18nStore, organizationStore, userStore} = useContext(StoreContext);

    useEffect(() => {
        async function handleMessage(event) {
            const {cacheName, updatedURL} = event.data.payload;

            const blockId = updatedURL.match(/\w{8}[-]\w{4}[-]\w{4}[-]\w{4}[-]\w{12}/)?.[0];

            if (blockId === blockObj.id && cacheName === "async-blocks") {
                const cache = await caches.open(cacheName);
                const updatedResponse = await cache.match(updatedURL, {ignoreVary: true});

                if (updatedResponse) {
                    const result = await updatedResponse.json();
                    if (result?.html && isJson(result.html)) {
                        runInAction(() => {
                            const newSettings = JSON.parse(result.html);
                            if (blockObj.setAttributes) {
                                blockObj.setAttributes({
                                    ...newSettings,
                                });
                            }
                        })
                    } else if (result.data) {
                        const contentSources = result.data;
                        if (contentSources?.length > 0) {
                            runInAction(() => {
                                blockObj.setAttributes({
                                    contentSources,
                                });
                            })
                        }
                    }
                }
            }
        }

        const conditionsToListen = !isPreviewBlock && blockObj.loadAsync && 'serviceWorker' in navigator;
        if (conditionsToListen) {
            navigator.serviceWorker.addEventListener("message", handleMessage);
        }

        return () => {
            if (conditionsToListen) {
                navigator.serviceWorker.removeEventListener("message", handleMessage);
            }
        }
    }, []);

    useAsyncEffect(async () => {
        if (!isPreviewBlock) {
            function legacyFinalizeBlock() {
                const blockID = blockObj.id;
                const task = {
                    orgID: organizationStore.currentOrganization.id,
                    url: DefaultController.blockFetch(i18nStore.currentOrgSupportedLocale, blockID),
                    data: {
                        'sizeX': blockObj.sizeX,
                        'sizeY': blockObj.sizeY,
                        'useAbsoluteUrls': true,
                        'usingReact': true,
                        'swcache': true, // caught be service worker
                    },
                    dataType: 'json',
                };

                ajaxGetQueue.push(task, 10, (err, data) => {
                    if (data?.html && isJson(data.html)) {
                        runInAction(() => {
                            const newSettings = JSON.parse(data.html);
                            if (blockObj.setAttributes) {
                                blockObj.setAttributes({
                                    ...newSettings,
                                    asyncLoaded: true,
                                });
                            }
                        })
                    }
                });
            }

            if (blockObj.blockType === "sharedfiles") {
                const client = await WatsonApi(blockObj.id, userStore.id);
                const task = {
                    url: client.organizations['organizations_content_sources_list'],
                    params: {
                        organization_pk: blockObj.id,
                        expand: ["items"],
                    },
                    options: {
                        clientAuthorizations: {
                            api_key: buildSwaggerAuthorization(blockObj.id, userStore.id, false, null),
                        }
                    }
                }
                watsonGetQueue.push(task, 10, result => {
                    const contentSources = JSON.parse(result.data).data;
                    if (contentSources?.length > 0) {
                        runInAction(() => {
                            blockObj.setAttributes({
                                contentSources,
                                asyncLoaded: true,
                            });
                        })
                    } else {
                        legacyFinalizeBlock();
                    }
                });
            } else if (blockObj.loadAsync) {
                // should only run for async blocks that haven't been converted over to Watson yet
                legacyFinalizeBlock();
            }
        }
    }, [blockObj.blockType]);
    // this is here so in ToolDrawerGrid -> ExampleBlock we update the block from loading to whatever when we get a new blockObj
    if (!isPreviewBlock && !blockObj.loadAsync && isJson(blockObj.htmlOutput)) {
        runInAction(() => {
            let newSettings = JSON.parse(blockObj.htmlOutput);
            const newBlockObj = newSettings?.blockObj;
            // for some reason the youtube/video blocks send blockObj as htmlOutput instead of filling the htmlOutput.
            if (newBlockObj) newSettings = {...newSettings, ...newBlockObj};
            if (blockObj.setAttributes) {
                blockObj.setAttributes({...newSettings});
            }
        })
    }
}
